"use client"

import { AddressRepo } from "@/core/repos/address.repo";
import container from "@/lib/di/container";
import { useState, useEffect } from "react";
import { DetailModal } from "./modals/DetailModal";
import { CreateContractModal } from "./modals/CreateContractModal";
import { Button, message, Popconfirm, Tooltip } from "antd";
import { DeleteOutlined, InfoCircleOutlined, FileAddOutlined } from "@ant-design/icons";
import { WaterRegistrationModel, WaterRegistrationModelFactory } from "@/core/models/water-registration/water-registration.model";
import { WaterRegistrationRepo } from "@/core/repos/tables/water-registration.repo";
import { getUnitTypeRegisterDisplay, getUseWaterRegisterStatusDisplay, UseWaterRegisterStatus } from "@/core/enums/global.enums";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { usePermissionStore } from "@/core/stores/permission.store";
import { FilterInputTemplate, FilterInputType, handleAddressChange, FilterBar } from "@/app/home/components/filter/FilterBar";
import { DataTable, FilterSearchPayload } from "@/app/home/components/table/DataTable";
import { DeleteButton } from "@/app/home/components/table/DeleteButton";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { useWaterRegisterStatusTagPipe } from "@/core/pipes/use-water-register-status.pipe";

const waterRegistrationRepo = container.get(WaterRegistrationRepo);
const addressRepo = container.get(AddressRepo);
const unitTypeRepo = container.get(UnitTypeRepo);

export default function WaterRegistrationPage() {
    const permissionStore = usePermissionStore();
    const [modalData, setModalData] = useState<WaterRegistrationModel>(WaterRegistrationModelFactory.getEmpty())
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(new Map())
    const [selectedRows, setSelectedRows] = useState<WaterRegistrationModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [isCreateContractModal, setIsCreateContractModal] = useState(false);
    const [contractRegistrationData, setContractRegistrationData] = useState<WaterRegistrationModel>(WaterRegistrationModelFactory.getEmpty());
    const [deleting, setDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>([
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "Nhập nội dung tìm kiếm",
            type: FilterInputType.text,
        },
        {
            dataIndex: "status",
            label: "Trạng thái",
            placeholder: "Chọn trạng thái",
            type: FilterInputType.select,
            options: [
                { name: getUseWaterRegisterStatusDisplay(UseWaterRegisterStatus.New), value: UseWaterRegisterStatus.New },
                { name: getUseWaterRegisterStatusDisplay(UseWaterRegisterStatus.InProcess), value: UseWaterRegisterStatus.InProcess },
                { name: getUseWaterRegisterStatusDisplay(UseWaterRegisterStatus.Completed), value: UseWaterRegisterStatus.Completed },
                { name: getUseWaterRegisterStatusDisplay(UseWaterRegisterStatus.Rejected), value: UseWaterRegisterStatus.Rejected },
            ],
        },
        {
            dataIndex: "unitTypeId",
            label: "Loại hình đơn vị",
            placeholder: "Chọn loại hình đơn vị",
            type: FilterInputType.select,
            options: [],
        },
        {
            dataIndex: "provinceId",
            label: "Tỉnh/ TP",
            placeholder: "Chọn Tỉnh/ TP",
            type: FilterInputType.select,
            options: [],
        },
        {
            dataIndex: "districtId",
            label: "Quận/ Huyện",
            placeholder: "Chọn Quận/ Huyện",
            type: FilterInputType.select,
            options: [],
        },
        {
            dataIndex: "wardId",
            label: "Xã/ Phường",
            placeholder: "Chọn Xã/ Phường",
            type: FilterInputType.select,
            options: [],
        },
        {
            rangeIndex: {from: "createdFrom", to: "createdTo"},
            type: FilterInputType.dateRange,
            placeholder: "Từ ngày",
            secondaryPlaceholder: "Đến ngày",
            label: "Ngày tạo",
            span: 2,
        },
    ])
    const titleData = useTitleDataStore();

    useEffect(() => {init()}, []);

    const populateSelections = async () => {
        const provinces = (await addressRepo.getAllProvinces()).map(data => ({name: data[1].name, value: data[0]}));
        const unitTypes = (await unitTypeRepo.getAll()).map(unitType => ({name: unitType.name, value: unitType.id}));
        const templates: FilterInputTemplate[] = [...filterTemplates];
        const provinceTemplate = templates.find(template => template.dataIndex === "provinceId");
        if (provinceTemplate) {
            provinceTemplate.options = provinces;
        }
        const unitTypeTemplate = templates.find(template => template.dataIndex === "unitTypeId");
        if (unitTypeTemplate) {
            unitTypeTemplate.options = unitTypes;
        }
        setFilterTemplates(templates);
    }

    const init = async () => {
        await populateSelections();
    }

    async function onFilterChange(map: Map<string, FilterSearchPayload>, changes: string[]) {
        await handleAddressChange(map, changes, filterTemplates, setFilterTemplates);
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: WaterRegistrationModel[]) {
        setSelectedRows([...rows]);
    }

    const showDetail = (record: WaterRegistrationModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const handleCreateContract = async (record: WaterRegistrationModel) => {
        // Load full detail if needed (to ensure we have all fields)
        const fullDetail = await waterRegistrationRepo.getDetail(record.id);
        if (fullDetail) {
            setContractRegistrationData(fullDetail);
        } else {
            setContractRegistrationData(record);
        }
        setIsCreateContractModal(true);
    };

    const handleContractCreated = () => {
        setIsCreateContractModal(false);
        messageApi.success({
            content: "Đã tạo hợp đồng thành công"
        });
    };

    const deleteSelected =  async () => {
        setDeleting(true);
        const result = await waterRegistrationRepo.deleteMany(selectedRows);
        messageApi.success({
            content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`
        })
        setDeleting(false);
    }

    return (
    <div className="px-4 py-6">
        {contextHolder}
        <FilterBar setMap={setFilterMap} onChange={onFilterChange} templates={filterTemplates} map={filterMap} actions={[
            <div key={1} className="mt-6">
                <DeleteButton loading={deleting} length={selectedRows.length} onConfirm={deleteSelected}/>
            </div>
        ]}/>
        <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
            <DataTable<WaterRegistrationModel>
                columns={[
                    { dataIndex: 'unitTypeId', name: 'Loại hình đơn vị', pipe: getUnitTypeRegisterDisplay},
                    { dataIndex: 'name', name: 'Tên' },
                    { dataIndex: 'mobilePhone', name: 'Điện thoại di động' },
                    { dataIndex: 'landlinePhone', name: 'Điện thoại cố định' },
                    { dataIndex: 'provinceName', name: 'Tỉnh/ TP' },
                    { dataIndex: 'districtName', name: 'Quận/ Huyện' },
                    { dataIndex: 'wardName', name: 'Phường/ Xã' },
                    { dataIndex: 'status', name: 'Trạng thái', pipe: useWaterRegisterStatusTagPipe},
                    {
                        name: 'Thao tác',
                        render: (_, record) => (
                                <div className="flex flex-row px-1 items-center justify-center gap-1">
                                    <Tooltip title="Xem chi tiết">
                                        <Button type="text" icon={<InfoCircleOutlined />} onClick={() => showDetail(record)}/>
                                    </Tooltip>
                                    <Tooltip title="Tạo hợp đồng">
                                        <Button 
                                            type="text" 
                                            icon={<FileAddOutlined />} 
                                            onClick={() => handleCreateContract(record)}
                                        />
                                    </Tooltip>
                                </div>
                            ),
                        fixed: "right"
                    },
                ]}
                repository={waterRegistrationRepo}
                filterMap={filterMap}
                onSelect={onRowSelect}
            ></DataTable>
        </div>
        <DetailModal isModalOpen={isDetailModal} setIsModalOpen={setDetailModal} data={modalData}></DetailModal>
        <CreateContractModal
            isModalOpen={isCreateContractModal}
            setIsModalOpen={setIsCreateContractModal}
            registrationData={contractRegistrationData}
            onSuccess={handleContractCreated}
        />
    </div>)
}